<template>
  <div class="add-package-modal-body">
    <v-toolbar class="modal-toolbar" density="comfortable" flat>
      <div class="modal-bar">
        <v-btn
          icon
          variant="text"
          class="back-btn"
          aria-label="Back"
          @click="modal.close()"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="modal-title">Add package</div>
      </div>
    </v-toolbar>

    <v-card-text class="body py-6">
      <v-form class="form" @submit.prevent="handleSubmit">
        <AppTextField
          v-model="schedule_package.name"
          label="Package name"
          placeholder="e.g., Basic Pest Control"
          :error-messages="nameError"
          @update:modelValue="nameError = ''"
        />
        <AppTextArea
          v-model="schedule_package.highlight"
          label="Highlight"
          placeholder="e.g., Exterior treatment + monthly follow-up"
          rows="3"
          :error-messages="highlightError"
          @update:modelValue="highlightError = ''"
        />
        <AppTextArea
          v-model="schedule_package.features"
          label="Features"
          placeholder="e.g., Spray, Inspection, Warranty"
          hint="Enter features separated by commas.."
          rows="4"
          :error-messages="featuresError"
          @update:modelValue="featuresError = ''"
        />

        <AppTextField
          v-model="schedule_package.price"
          label="Price"
          type="number"
          placeholder="0.00"
          prepend-inner-icon="mdi-currency-usd"
          hide-details
          :error-messages="pricingError"
          @update:modelValue="pricingError = ''"
        />

        <div class="actions">
          <AppButtonFlat
            block
            @click="handleSubmit"
            prepend-icon="mdi-check"
            color="primary"
          >
            Create
          </AppButtonFlat>
        </div>
      </v-form>
    </v-card-text>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppTextArea from "@/shared/components/ui/AppTextArea.vue";
import { useModalStore } from "@/shared/stores/modal";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";

type SchedulePackage = {
  id?: number | string;
  name: string;
  highlight: string;
  features: string;
  price: number | string;
  package_type: "one_time";
};

const props = defineProps<{
  onAdd?: (pkg: SchedulePackage) => void;
}>();

const modal = useModalStore();

const schedule_package = reactive<SchedulePackage>({
  name: "",
  highlight: "",
  features: "",
  price: "",
  package_type: "one_time",
});

// Error messages for validation
const nameError = ref("");
const highlightError = ref("");
const featuresError = ref("");
const pricingError = ref("");

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function generateId() {
  const base = slugify(schedule_package.name) || "package";
  return `${base}_${Date.now()}`;
}

function handleSubmit() {
  // Clear previous errors
  nameError.value = "";
  highlightError.value = "";
  featuresError.value = "";
  pricingError.value = "";

  const pkgName = schedule_package.name.trim();
  if (!pkgName) {
    nameError.value = "Package name is required";
    return;
  }

  const pkgHighlight = schedule_package.highlight.trim();
  if (!pkgHighlight) {
    highlightError.value = "Package highlight is required";
    return;
  }

  const pkgFeatures = schedule_package.features.trim();
  if (!pkgFeatures) {
    featuresError.value = "Package features are required";
    return;
  }

  const numericPrice = schedule_package.price === "" ? null : Number(schedule_package.price);
  if (numericPrice === null || Number.isNaN(numericPrice) || numericPrice <= 0) {
    pricingError.value = "Fixed price is required and must be greater than 0.";
    return;
  }

  const pkg: SchedulePackage = {
    name: pkgName,
    highlight: pkgHighlight,
    features: pkgFeatures,
    price: numericPrice,
    package_type: "one_time",
  };

  props.onAdd?.(pkg);

  modal.close();
}
</script>

<style scoped>
.add-package-modal-body {
  padding: 0 0 calc(env(safe-area-inset-bottom, 0px) + 16px);
  min-height: 100dvh;
}

.modal-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-theme);
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 0 0;
  height: auto;
}

.modal-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
}

:deep(.modal-toolbar .v-toolbar__content) {
  height: auto;
  min-height: 64px;
  align-items: center;
  padding: 8px 16px;
}

.back-btn {
  width: 50px;
  height: 50px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.body {
  padding-top: 12px;
}

.form {
  display: grid;
  gap: 16px;
}

.section-title {
  margin-top: 8px;
  font-weight: 600;
  color: #374151;
}

.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.service-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
  cursor: pointer;
}

.service-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.service-title {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.service-subtitle {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.3;
}

.service-check {
  margin: 0;
}

.pricing-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pricing-item {
  padding: 8px 0;
}

.pricing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.pricing-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.delete-btn {
  opacity: 0.7;
  transition: opacity 0.2s;
}

.delete-btn:hover {
  opacity: 1;
}

.pricing-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.pricing-field {
  flex: 1;
}

.add-pricing-btn {
  margin-top: 8px;
}

.pricing-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.pricing-row .v-select {
  flex: 1;
}

.pricing-row .app-text-field {
  flex: 1;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.api-error {
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
}
</style>
